/*
 * Klient API frontendu prawly. — zastępuje wcześniejszy mock (src/api.js).
 * Ta sama sygnatura api(path, {method, body}), więc strony nie wymagają zmian.
 * Adres serwera: REACT_APP_API_URL (projekt stoi na react-scripts, nie na Vite).
 */

const BASE =
  process.env.REACT_APP_API_URL || "https://prawlyapi.a2synergy.pl/api";
const SES_KEY = "prawly_session_v3";

/** „czeka od 5 godz." — czas oczekiwania od podanej chwili (zapytania klienta po obsłudze sprawy). */
export function waitingText(since) {
  const h = Math.floor((Date.now() - new Date(since).getTime()) / 3600000);
  if (!Number.isFinite(h) || h < 0) return '';
  if (h < 1) return 'czeka krócej niż godzinę';
  if (h < 48) return `czeka od ${h} godz.`;
  return `czeka od ${Math.floor(h / 24)} dni`;
}

export const zl = (cents) =>
  ((cents || 0) / 100).toLocaleString("pl-PL", {
    style: "currency",
    currency: "PLN",
  });

export const STATUS = {
  new: { label: "Oczekuje na płatność", tone: "muted" },
  ai_generating: { label: "Analiza w toku", tone: "info" },
  ai_draft: { label: "Czeka na prawnika", tone: "warn" },
  ai_failed: { label: "Wymaga uwagi", tone: "warn" },
  in_review: { label: "W opracowaniu", tone: "info" },
  followup_pending: { label: "Pytanie klienta", tone: "warn" },
  followup_payment: { label: "Oczekuje na dopłatę", tone: "muted" },
  delivered: { label: "Gotowe", tone: "ok" },
  declined: { label: "Odrzucona", tone: "warn" },
};

export const KIND = {
  opinion: { label: "Opinia prawna", noun: "porada" },
  document: { label: "Dokument prawny", noun: "dokument" },
  bundle: { label: "Pakiet: opinia + dokument", noun: "pakiet" },
  custom: { label: "Usługa indywidualna", noun: "sprawa" },
};

/* Pobranie pliku spod endpointu wymagającego autoryzacji — ten sam wzorzec
   fetch+blob co downloadFile w components/Files.jsx (autoryzacja przez
   nagłówek, nie przez URL, więc pliku nie da się po prostu wkleić jako
   <a href>). Współdzielone przez pobieranie faktur i samego pisma (DOCX). */
async function downloadAuthed(path, filename, failMsg) {
  const s = session.get();
  const res = await fetch(`${BASE}${path}`, {
    headers: s?.token ? { Authorization: `Bearer ${s.token}` } : {},
  });
  if (!res.ok) {
    let msg = `${failMsg} (${res.status})`;
    try {
      msg = (await res.json()).error || msg;
    } catch {
      /* nic */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadInvoice(caseId, paymentId, filename) {
  return downloadAuthed(`/cases/${caseId}/invoice/${paymentId}`, filename, "Nie udało się pobrać faktury");
}

export function downloadAnswerDocx(caseId, answerId, filename) {
  return downloadAuthed(`/cases/${caseId}/answers/${answerId}/docx`, filename, "Nie udało się pobrać pisma");
}

export const LEGAL_TITLE_LABELS = {
  adwokat: "adwokat",
  radca_prawny: "radca prawny",
  aplikant: "aplikant",
};

export const session = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(SES_KEY));
    } catch {
      return null;
    }
  },
  set(data) {
    localStorage.setItem(SES_KEY, JSON.stringify(data));
  },
  clear() {
    localStorage.removeItem(SES_KEY);
  },
};

export async function api(path, { method = "GET", body } = {}) {
  const s = session.get();
  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        // Content-Type tylko tam, gdzie jest treść: na GET niepotrzebny nagłówek wymuszał dodatkowe
        // zapytanie preflight (CORS) przed każdym pobraniem treści i cennika — ok. 0,7 s na telefonie.
        ...(method === "GET" ? {} : { "Content-Type": "application/json" }),
        ...(s?.token ? { Authorization: `Bearer ${s.token}` } : {}),
      },
      body: method === "GET" ? undefined : JSON.stringify(body || {}),
    });
  } catch {
    // brak odpowiedzi (serwer niedostępny, zerwane połączenie, przekroczony czas) — czytelny komunikat zamiast "Failed to fetch"
    throw new Error("Nie udało się połączyć z serwerem. Sprawdź połączenie i spróbuj ponownie za chwilę.");
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    /* pusta odpowiedź */
  }

  if (res.status === 401) {
    // Token wygasł albo został unieważniony. Czyścimy sesję i dajemy znać
    // aplikacji, żeby przerysowała nawigację bez przeładowania strony.
    const hadSession = Boolean(s);
    session.clear();
    if (hadSession) window.dispatchEvent(new Event("prawly:session-expired"));
    throw new Error(data.error || "Sesja wygasła — zaloguj się ponownie.");
  }
  if (res.status === 429)
    throw new Error(data.error || "Zbyt wiele żądań. Spróbuj ponownie za chwilę.");
  if (!res.ok) throw new Error(data.error || `Błąd serwera (${res.status})`);
  return data;
}

/** Wymagania hasła — muszą odpowiadać checkPasswordStrength w prawly_server/auth.js. */
export const PASSWORD_HINT =
  "Minimum 10 znaków, w tym przynajmniej jedna litera i jedna cyfra.";

export function checkPassword(v) {
  if (typeof v !== "string" || v.length < 10)
    return "Hasło musi mieć co najmniej 10 znaków.";
  if (!/[a-zA-Zà-žÀ-Ž]/.test(v) || !/\d/.test(v))
    return "Hasło musi zawierać przynajmniej jedną literę i jedną cyfrę.";
  return null;
}

/* Przy prawdziwym backendzie reset danych robi się przez schema.sql;
   przycisk w stopce czyści tylko lokalną sesję. */
export function resetDemo() {
  session.clear();
}
